// Het chatgesprek overleeft een paginawissel of herlaad.
//
// Tot 19-10-2026 stond het gesprek alleen in het geheugen van de widget en
// begon elke pagina met alleen het welkomstbericht. De livechat-poll had dan
// geen cursor meer, haalde de hele teamgeschiedenis opnieuw op en zette een
// oud "Goedemiddag" van het team bij elke pagina weer in beeld. Nu bewaren we
// het gesprek per chatsessie en de poll-cursor per Twilper-gesprek, en
// herkennen we teamberichten aan hun Twilper-id in plaats van aan hun tekst
// (een tweede "ok" of "bedankt" is een nieuw bericht, geen dubbel).

import { insertChronologically } from '@/lib/chatbot/human-pacing';

export const TRANSCRIPT_KEY_PREFIX = 'printen_chat_transcript_';
export const LIVE_CURSOR_KEY_PREFIX = 'printen_chat_live_cursor_';
export const TRANSCRIPT_MAX_MESSAGES = 50;
export const TRANSCRIPT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type TranscriptMessage = {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  at?: number;
  /** Twilper-id van een teambericht uit de livechat-poll. */
  liveId?: string;
  /** Uit /api/chatbot/history geladen: heeft geen Twilper-id. */
  fromHistory?: boolean;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

type StoredTranscript<T> = { savedAt: number; messages: T[] };

/** Leest het bewaarde gesprek van deze chatsessie, of null als er niets (geldigs) is. */
export function loadTranscript<T extends TranscriptMessage>(
  resolveStorage: () => StorageLike,
  sessionId: string,
  now: number = Date.now()
): T[] | null {
  try {
    const raw = resolveStorage().getItem(TRANSCRIPT_KEY_PREFIX + sessionId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredTranscript<T>;
    if (!parsed || typeof parsed.savedAt !== 'number' || !Array.isArray(parsed.messages)) return null;
    if (now - parsed.savedAt > TRANSCRIPT_MAX_AGE_MS) return null;
    const messages = parsed.messages.filter(
      (message) => message && typeof message.id === 'string' && typeof message.content === 'string'
        && (message.role === 'assistant' || message.role === 'user')
    );
    return messages.length ? messages : null;
  } catch {
    return null;
  }
}

/** Bewaart de laatste berichten van deze sessie en ruimt verlopen gesprekken op. */
export function saveTranscript<T extends TranscriptMessage>(
  resolveStorage: () => StorageLike,
  sessionId: string,
  messages: T[],
  now: number = Date.now()
): void {
  try {
    const storage = resolveStorage();
    const payload: StoredTranscript<T> = { savedAt: now, messages: messages.slice(-TRANSCRIPT_MAX_MESSAGES) };
    storage.setItem(TRANSCRIPT_KEY_PREFIX + sessionId, JSON.stringify(payload));
    pruneExpired(storage, now);
  } catch {
    // Opslag kan geblokkeerd of vol zijn; het gesprek in beeld blijft werken.
  }
}

function pruneExpired(storage: StorageLike, now: number) {
  const expired: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key?.startsWith(TRANSCRIPT_KEY_PREFIX)) continue;
    try {
      const parsed = JSON.parse(storage.getItem(key) || 'null') as StoredTranscript<unknown> | null;
      if (!parsed || typeof parsed.savedAt !== 'number' || now - parsed.savedAt > TRANSCRIPT_MAX_AGE_MS) expired.push(key);
    } catch {
      expired.push(key);
    }
  }
  expired.forEach((key) => storage.removeItem(key));
}

/** Laatste teambericht (Twilper-id) dat al in het gesprek staat; `after_id` voor de poll. */
export function loadLiveCursor(resolveStorage: () => Pick<Storage, 'getItem'>, conversationId: string): string {
  try {
    return resolveStorage().getItem(LIVE_CURSOR_KEY_PREFIX + conversationId) || '';
  } catch {
    return '';
  }
}

export function saveLiveCursor(resolveStorage: () => Pick<Storage, 'setItem' | 'removeItem'>, conversationId: string, afterId: string): void {
  try {
    const storage = resolveStorage();
    if (afterId) storage.setItem(LIVE_CURSOR_KEY_PREFIX + conversationId, afterId);
    else storage.removeItem(LIVE_CURSOR_KEY_PREFIX + conversationId);
  } catch {
    // Zonder opslag haalt de volgende pagina de geschiedenis op; de id-check voorkomt dubbelen.
  }
}

export type LiveMessage = { liveId?: string; content: string; at?: number };

/** Staat dit teambericht al in beeld (of in de wachtrij)? */
export function isKnownLiveMessage(messages: TranscriptMessage[], live: LiveMessage): boolean {
  if (live.liveId) {
    if (messages.some((message) => message.liveId === live.liveId)) return true;
    // Uit de databasegeschiedenis geladen berichten hebben geen Twilper-id.
    return messages.some((message) => message.fromHistory && message.role === 'assistant' && message.content === live.content);
  }
  // Zonder id (oude Twilper-versie) blijft alleen de tekst over.
  return messages.some((message) => message.role === 'assistant' && message.content === live.content);
}

/** Zet een teambericht op zijn plek in de tijdlijn, tenzij het er al staat. */
export function mergeLiveMessage<T extends TranscriptMessage>(messages: T[], message: T): T[] {
  if (isKnownLiveMessage(messages, message)) return messages;
  return insertChronologically(messages, message);
}
